/**
 * The HTTP core.
 *
 * Session state lives in an httpOnly cookie, so there is no token in
 * JavaScript for a script injection to steal and nothing to synchronise
 * between tabs. Every call sends credentials; the dev server proxies /api to
 * the API so the browser sees one origin.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
    readonly issues?: { path: string; message: string }[],
    readonly payload?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the user simply is not signed in (not a real failure). */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

export class NetworkError extends Error {
  constructor() {
    super('network');
    this.name = 'NetworkError';
  }
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'include',
      ...init,
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        // The desktop gateway answers in the app's language when it answers offline.
        'Accept-Language': document.documentElement.lang || 'en',
        ...(init.headers as Record<string, string> | undefined),
      },
    });
  } catch {
    throw new NetworkError();
  }

  if (res.status === 204) return undefined as T;

  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { message: text };
    }
  }

  if (!res.ok) {
    const payload = (body ?? {}) as {
      message?: string | { message?: string; code?: string; issues?: { path: string; message: string }[] };
      code?: string;
      issues?: { path: string; message: string }[];
    };
    // Nest wraps a thrown object under `message`; unwrap it so the caller sees
    // a consistent shape regardless of how the endpoint reported the problem.
    const nested = typeof payload.message === 'object' && payload.message !== null ? payload.message : null;
    const code = nested?.code ?? payload.code ?? null;
    const message =
      (nested?.message ?? (typeof payload.message === 'string' ? payload.message : null)) ??
      `Request failed (${res.status})`;
    throw new ApiError(
      res.status,
      code,
      message,
      nested?.issues ?? payload.issues,
      (nested ?? payload) as Record<string, unknown>,
    );
  }

  return body as T;
}
